import { useEffect, useRef } from 'react';
import type { MailActionOptions } from 'shared/analytics/capture-event';

type ShortcutActions = {
  isReady: boolean;
  canReply: boolean;
  canAssign: boolean;
  canGoPrev: boolean;
  canGoNext: boolean;
  openReply: () => void;
  toggleStatus: (options: MailActionOptions) => void;
  openAssign: (options: MailActionOptions) => void;
  goPrev: (options: MailActionOptions) => void;
  goNext: (options: MailActionOptions) => void;
};

export const useMailDetailShortcuts = (actions: ShortcutActions) => {
  const actionsRef = useRef<ShortcutActions>({
    isReady: false,
    canReply: false,
    canAssign: false,
    canGoPrev: false,
    canGoNext: false,
    openReply: () => {},
    toggleStatus: () => {},
    openAssign: () => {},
    goPrev: () => {},
    goNext: () => {},
  });

  // Kept as a ref assignment (not setState) so the event handler always reads
  // the latest values without re-registering the listener on every render.
  actionsRef.current = actions;

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      if (target.isContentEditable) return;

      const handlers = actionsRef.current;
      if (!handlers.isReady) return;

      if (event.key === 'r' && handlers.canReply) {
        event.preventDefault();
        handlers.openReply();
      } else if (event.key === 'e') {
        event.preventDefault();
        handlers.toggleStatus({ trigger: 'shortcut' });
      } else if (event.key === 'a' && handlers.canAssign) {
        event.preventDefault();
        handlers.openAssign({ trigger: 'shortcut' });
      } else if (event.key === 'k' && handlers.canGoPrev) {
        event.preventDefault();
        handlers.goPrev({ trigger: 'shortcut' });
      } else if (event.key === 'j' && handlers.canGoNext) {
        event.preventDefault();
        handlers.goNext({ trigger: 'shortcut' });
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
};
